import { createFileRoute, Link } from '@tanstack/react-router'
import { PageHeader } from '@/components/shared/page-header'
import { useState } from 'react'
import { AlertCircle, Network } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useReferrals } from '@/hooks'
import { AppPagination } from '@/components/shared/app-pagination'
import { ReferralFilters } from '@/components/referrals/referral-filters'
import { ReferralTable } from '@/components/referrals/referral-table'
import type { SortDirection } from '@/components/shared/sortable-header'

export const Route = createFileRoute('/_app/referrals/')({
  component: ReferralsPage,
})

function ReferralsPage() {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
  })
  const [sortKey, setSortKey] = useState<string | null>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Reset page when filters change
  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null)
    setSortDirection(direction)
    setPage(1)
  }

  const {
    data: queryData,
    isLoading,
    error,
  } = useReferrals(
    page,
    20,
    {
      search: filters.search,
      status: filters.status === 'all' ? undefined : filters.status,
      source: filters.source === 'all' ? undefined : filters.source,
    },
    { sortBy: sortKey, sortDirection }
  )

  const referrals = queryData?.data
  const meta = queryData?.meta

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <p className="text-muted-foreground">Erro ao carregar indicações</p>
          <p className="text-sm text-destructive">{error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Indicações"
        description="Acompanhe todas as vendas por indicação"
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/referrals/network">
              <Network className="h-4 w-4" />
              Rede
            </Link>
          </Button>
        }
      />

      <ReferralFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <ReferralTable
        data={referrals}
        isLoading={isLoading}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSort={handleSort}
        onClearFilters={() => handleFiltersChange({ search: '', status: 'all', source: 'all' })}
      />

      <AppPagination meta={meta} onPageChange={setPage} isLoading={isLoading} />
    </div>
  )
}
